import { isTauri } from "@tauri-apps/api/core";
import { Store } from "@tauri-apps/plugin-store";
import {
  nextLoadDelayMs,
  readStoreValue,
  REFRESH_INTERVAL_MS,
  SharedCache,
  TMP_STORE_PATH,
} from "./shared-memory";

const cache = new SharedCache();
let started = false;
let storePromise: Promise<Store> | null = null;
let tail: Promise<void> = Promise.resolve();

function sleep(delayMs: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, delayMs));
}

function openStore(): Promise<Store> {
  if (storePromise == null) {
    storePromise = Store.load(TMP_STORE_PATH, { autoSave: true });
  }
  return storePromise;
}

/** Store commands run one at a time so a read cannot land in the middle of a write. */
function runExclusive(task: () => Promise<void>): Promise<void> {
  const run = tail.then(task, task);
  tail = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}

async function loadStore(): Promise<Store> {
  let attempt = 0;
  while (true) {
    try {
      return await openStore();
    } catch (error) {
      storePromise = null;
      console.debug("Shared storage open failed", error);
      await sleep(nextLoadDelayMs(attempt));
      attempt += 1;
    }
  }
}

/**
 * Copy the whole store into the cache.
 * `entries()` reads the plugin's in-memory map, which every window shares.
 * The file is the saved copy of that map. This does not call `reload()`,
 * because a reload reads the file and the file can lag a write by the
 * auto-save delay.
 */
async function pull(store: Store): Promise<void> {
  const readRevision = cache.revisionAt();
  const entries = await store.entries<unknown>();
  const retry = cache.applyEntries(entries, readRevision);
  for (const entry of retry) {
    if (cache.pendingValue(entry.key) !== entry.value) {
      continue;
    }
    await store.set(entry.key, entry.value);
  }
}

async function initialize(): Promise<void> {
  if (started) {
    return;
  }
  started = true;
  const store = await loadStore();
  await store.onChange((key, value) => {
    const parsed = readStoreValue(value);
    if (parsed === undefined) {
      return;
    }
    cache.applyRemote(key, parsed);
  });
  await runExclusive(() => pull(store));
  while (true) {
    await sleep(REFRESH_INTERVAL_MS);
    try {
      await runExclusive(() => pull(store));
    } catch (error) {
      console.debug("Shared storage refresh failed", error);
    }
  }
}

if (isTauri()) {
  void initialize();
}

const nativeSharedStorage = {
  setItem: (key: string, value: string | number) => {
    const stored = String(value);
    cache.remember(key, stored);
    if (!isTauri()) {
      return;
    }
    void runExclusive(async () => {
      const store = await openStore();
      if (cache.pendingValue(key) !== stored) {
        return;
      }
      await store.set(key, stored);
    }).catch((error) => {
      console.debug("Shared storage write failed", error);
    });
  },
  getItem: (key: string): string | null => {
    return cache.get(key);
  },
};

const localStorageFallback = {
  setItem: (key: string, value: string | number) => {
    localStorage.setItem(`sharedStorage_${key}`, String(value));
  },
  getItem: (key: string): string | null => {
    return localStorage.getItem(`sharedStorage_${key}`);
  },
};

export const NativeSharedStorage = isTauri() ? nativeSharedStorage : localStorageFallback;
